<template>
  <CardWrapper
    :class="[
      'withdrawal-widget',
      { 'grey': tronColorGrey }
    ]"
    data-test-id="withdrawal-widget"
    :title="widgetTitle"
    :key="widgetKey"
  >
    <div
      v-show="isFillingMode && tokenCodes.length"
      :class="{ 'invisible': !isFillingMode }"
    >
      <!-- Вибір актива -->
      <SelectAsset
        :stepNumber="1"
        :isActiveStep="checkActiveStep(1)"
        :tokenCodes="tokenCodes"
        @setStep="(step) => setCurrentStep(step)"
        @onAssetSelected="onAssetSelectedHandler"
        @setTronColor="(e) => tronColorGrey = e"
        @selectIsOpen="(e) => assetSelectIsOpen = e"
      />

      <!-- Ввод суми -->
      <FillAmount
        :stepNumber="2"
        :isActiveStep="checkActiveStep(2)"
        :selectedAsset="selectedAsset || ''"
        :curentSelectedTocken="curentSelectedTocken || ''"
        :minAmount="minAmount"
        :limitList="limitList"
        :allFees="allFees"
        :isFeesLoadPending="isFeesLoadPending"
        :willCharged="willCharged"
        :showTooltip="!assetSelectIsOpen"
        @setStep="(step) => setCurrentStep(step)"
        @onFieldBlur="checkLimit"
        @setAmount="onSetAmountHandler"
        @setFeesInZero="setFeesInZero"
        @setAmountFieldError="(e) => amountHasError = e"
      />

      <!-- Адреса гаманця -->
      <FillWalletAddress
        :stepNumber="3"
        :isActiveStep="checkActiveStep(3)"
        :selectedAssetWithoutSpace="selectedAssetWithoutSpace || ''"
        @setStep="(step) => setCurrentStep(step)"
        @setWalletAddress="processTransfer"
      />
    </div>

    <ConfirmStep
      v-if="isConfirmMode"
      :selectedAsset="selectedAsset"
      :options="requestPayload"
      :isPending="withdrawalReqPending"
      @cancel-click="onCancelClickHandler"
      @confirm-click="makeWithdrawal"
    />

    <WithdrawalSuccess
      v-if="isSuccessMode"
      :selectedAsset="selectedAsset"
      :amount="amount"
      @resetWidget="resetWidget"
    />

    <EmptyWidgetStub v-if="!tokenCodes.length" />
  </CardWrapper>
</template>

<script>
  import { i18n } from '@js/i18n'
  import debounce from 'lodash/debounce'
  import { getTokenNetwork, isNetworkToken } from '@utils/consts'
  import { ACCOUNT_TYPES } from '@const'
  import { transactionType } from '@js/const/limits.const'
  import { DECIMAL_PLACES } from '@utils/math.util'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { errors } from '@js/errors/factory'
  import { ErrorHandler } from '@js/errors/error_handler'

  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'

  import formMixin from '@com/mixins/form.mixin'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  import CardWrapper from '@com/UI/CardWrapper.vue'
  import SelectAsset from './SelectAsset.vue'
  import FillAmount from './FillAmount.vue'
  import FillWalletAddress from './FillWalletAddress.vue'
  import ConfirmStep from './ConfirmStep.vue'
  import WithdrawalSuccess from './WithdrawalSuccess.vue'
  import EmptyWidgetStub from './EmptyWidgetStub.vue'

  import { usersService } from '@api/users.service'
  import { feeService } from '@api/fees.service'
  import { withdrawService } from '@api/withdraw.service'
  import { limitsService } from '@api/limits.service'

  const WIDGET_STEPS = {
    filling: 'filling',
    confirm: 'confirm',
    success: 'success'
  }

  export default {
    name: 'WithdrawalWidget',
    components: {
      CardWrapper,
      SelectAsset,
      FillAmount,
      FillWalletAddress,
      ConfirmStep,
      WithdrawalSuccess,
      EmptyWidgetStub
    },
    mixins: [formMixin, CurrencyRound],
    emits: ['refreshBalances'],
    data () {
      return {
        WIDGET_STEPS,
        i18n,
        widgetKey: 0,
        widgetMode: WIDGET_STEPS.filling,
        currentStep: 1,
        selectedAsset: null,
        tronColorGrey: false,
        amount: '',
        minAmounts: {},
        loadedMinAmountAssets: {},
        amountHasError: false,
        walletAddress: '',
        limitList: [],
        allFees: {}, // получение установленной комиссии для определенной валюты
        allFeesLoaded: false,
        isFeesLoadPending: false,
        isFeesLoadFailed: false,
        feesDebouncedRequest: null,
        fixedFee: '',
        percentFee: '',
        willCharged: '',
        assetSelectIsOpen: false,
        requestPayload: {},
        withdrawalReqPending: false
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.userWithdrawableTokens,
        vuexTypes.accountTypeI,
        vuexTypes.accountSignerName,
        vuexTypes.accountBalances,
        vuexTypes.accountId,
        vuexTypes.walletId
      ]),
      isFillingMode () {
        return this.widgetMode === WIDGET_STEPS.filling
      },
      isConfirmMode () {
        return this.widgetMode === WIDGET_STEPS.confirm
      },
      isSuccessMode () {
        return this.widgetMode === WIDGET_STEPS.success
      },
      widgetTitle () {
        if (this.isFillingMode) return i18n.withdraw_withdrawal()
        if (this.isConfirmMode) return i18n.withdraw_confirm()
        if (this.isSuccessMode) return i18n.withdraw_withdrawal()
      },
      tokenCodes () {
        return this.userWithdrawableTokens
          .map(token => token.code)
          .filter(el => !el.endsWith('20D'))
      },
      selectedAssetWithoutSpace () {
        return this.selectedAsset && this.selectedAsset.replace(' ', '')
      },
      isTron () {
        // Historical name: "tron" here means any network token with a grey (D) counterpart
        return isNetworkToken(this.selectedAssetWithoutSpace)
      },
      isGreyTron () {
        if (this.isTron && this.tronColorGrey) return `${this.selectedAssetWithoutSpace}D`
        return false
      },
      curentSelectedTocken () {
        return this.isGreyTron
          ? this.isGreyTron
          : this.selectedAssetWithoutSpace
      },
      minAmount () {
        const asset = this.selectedAssetWithoutSpace
        if (!asset) return null

        return this.minAmounts[asset] || null
      },
      commissions () {
        const name = this.accountSignerName
        return name ? name.toLowerCase().startsWith('commission', 0) : false
      },
      balance () {
        return this.accountBalances[this.curentSelectedTocken]
      }
    },
    methods: {
      checkActiveStep (step) {
        return this.currentStep === step
      },
      async setCurrentStep (step) {
        if ((step === 2 || step === 3) && !this.selectedAsset) return
        if (step === 3 &&
          (this.amount === '' || this.amount.includes('NaN') || this.amountHasError)
        ) {
          this.currentStep = 2
          return
        }
        this.currentStep = step
      },
      onAssetSelectedHandler (asset) {
        this.selectedAsset = asset
        if (!asset) return

        this.loadSelectedAssetMinAmount(this.selectedAssetWithoutSpace)
        this.checkLimit()

        if (!+this.amount) return
        this.allFeesLoaded = false
        this.tryGetFees()
      },
      onSetAmountHandler (amount) {
        this.amount = amount
        if (!+this.amount) return
        this.tryGetFees()
      },
      getWithdravalType () {
        let withdravalType = ''
        // Any network token (grey included) goes through the usdt withdrawal pipeline
        if (getTokenNetwork(this.selectedAssetWithoutSpace)) {
          withdravalType = 'usdt'
        }
        return withdravalType
      },
      async checkLimit () {
        if (!this.selectedAssetWithoutSpace) return
        this.limitList = await usersService.getLimit(
          this.curentSelectedTocken,
          transactionType.withdrawal,
          this.getWithdravalType()
        )
      },
      async getAccountFees (token) {
        try {
          const getAllFees = await feeService.loadAccountFees()
          Object.values(getAllFees)
            .forEach(fees => fees.forEach(fee => {
              if (fee.asset === token && fee.fee_type === 2) {
                this.allFees = {
                  percent: Number(fee.percent).toFixed(DECIMAL_PLACES),
                  fixed: Number(fee.fixed).toFixed(DECIMAL_PLACES)
                }
              }
            }))
          this.allFeesLoaded = true
        } catch (error) {
          console.error(error)
        }
      },
      async getFees () {
        try {
          const fees = await feeService.loadWithdrawalFeeByAmount(this.curentSelectedTocken, this.amount)
          this.fixedFee = this.commissions ? 0 : Number(fees.fixed).toFixed(DECIMAL_PLACES)
          this.percentFee = this.commissions ? 0 : Number(fees.percent).toFixed(DECIMAL_PLACES)
          this.isFeesLoadFailed = false
        } catch (err) {
          this.isFeesLoadFailed = true
          EventDispatcher.dispatchShowErrorEvent(i18n.fees_load_fail())
        }

        this.isFeesLoadPending = false
        this.willCharged = this.roundCurrency(
          (parseFloat(this.fixedFee) +
            parseFloat(this.percentFee) +
            parseFloat(this.amount)
          ).toFixed(DECIMAL_PLACES),
          this.curentSelectedTocken,
          false
        )

        if (!this.allFeesLoaded) {
          await this.getAccountFees(this.curentSelectedTocken)
        }
      },
      async tryGetFees () {
        if (!this.selectedAssetWithoutSpace || this.accountTypeI === ACCOUNT_TYPES.commission) return

        this.isFeesLoadPending = true
        if (!this.feesDebouncedRequest) {
          this.feesDebouncedRequest = debounce(() => this.getFees(), 300)
        }
        return this.feesDebouncedRequest()
      },
      async onSetTronWhiteHandler () {
        this.allFeesLoaded = false
        await this.loadSelectedAssetMinAmount(this.selectedAssetWithoutSpace)
        await this.checkLimit()
        await this.tryGetFees()
      },
      async onSetTronGreyHandler () {
        this.allFeesLoaded = false
        await this.loadSelectedAssetMinAmount(this.selectedAssetWithoutSpace)
        await this.checkLimit()
        await this.tryGetFees()
      },
      async loadSelectedAssetMinAmount (asset) {
        if (!asset) return
        if (this.loadedMinAmountAssets[asset]) return

        const response = await limitsService.getMinimumLimits(asset)
        if (response && response.error) {
          if (limitsService.isAssetSettingsNotFoundError(response.error)) {
            this.$set(this.loadedMinAmountAssets, asset, true)
          }
          return
        }

        const rawMinLimit = response && response.data
          ? response.data.withdrawal_min_limit
          : null
        if (!rawMinLimit) {
          this.$set(this.loadedMinAmountAssets, asset, true)
          return
        }

        const minAmount = limitsService.getMinAmountFromAssetSettings(
          response && response.data,
          asset,
          'withdrawal'
        )
        const formattedMinAmount = limitsService.formatMinAmount(minAmount)

        if (formattedMinAmount) {
          this.$set(this.minAmounts, asset, formattedMinAmount)
        }

        this.$set(this.loadedMinAmountAssets, asset, true)
      },
      setFeesInZero () {
        this.fixedFee = '0.00'
        this.percentFee = '0.00'
      },
      getWithdrawalPayload () {
        const internalId = crypto.randomUUID()
        const withdravalType = this.getWithdravalType()

        const payload = {
          balance: this.balance.balance_id,
          sender_user_id: this.accountId,
          receiver_account: this.isGreyTron ? undefined : this.walletAddress,
          sub_wallet_id: null,
          externalDetails: {
            address: this.walletAddress,
            withdrawalType: withdravalType,
            displayName: '',
            internalId: internalId,
            wallet: this.walletId
          },
          destAsset: this.curentSelectedTocken,
          fee: {
            fixed: this.fixedFee,
            percent: this.percentFee
          },
          amount: this.roundCurrency2(this.amount, this.selectedAssetWithoutSpace),
          expectedDestAssetAmount: this.roundCurrency2(this.amount, this.selectedAssetWithoutSpace)
        }

        return payload
      },
      async processTransfer (wallet) {
        this.walletAddress = wallet
        if (this.amountHasError) return
        this.disable()

        try {
          this.requestPayload = this.getWithdrawalPayload()
          this.widgetMode = WIDGET_STEPS.confirm
        } catch (error) {
          console.log(error)
          if (error instanceof errors.NotFoundError) {
            error.showBanner(i18n.withdraw_error_invalid_address())
            await this.enable()
            return
          }
          ErrorHandler.processUnexpected(error)
        }
        this.enable()
      },
      onCancelClickHandler () {
        this.widgetMode = WIDGET_STEPS.filling
      },
      async makeWithdrawal () {
        if (this.amountHasError || !this.walletAddress) return
        this.disableLong()
        this.withdrawalReqPending = true

        try {
          if (this.isFeesLoadFailed) {
            EventDispatcher.dispatchShowErrorEvent(i18n.fees_load_fail())
            return false
          }
          const payload = this.getWithdrawalPayload()
          await withdrawService.createWithdrawalRequest(payload)
          EventDispatcher.dispatchShowErrorEvent(i18n.withdraw_usdt_delay_msg())

          this.widgetMode = WIDGET_STEPS.success
          this.$emit('refreshBalances')
        } catch (error) {
          if (error instanceof errors.TFAExceededCodeError) {
            EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
          } else {
            ErrorHandler.processUnexpected(error)
          }
        }

        this.enable()
        this.errors.clear()
        this.withdrawalReqPending = false
      },
      resetWidget () {
        if (this.feesDebouncedRequest) this.feesDebouncedRequest.cancel()
        this.clear()
        this.widgetKey += 1
        this.widgetMode = WIDGET_STEPS.filling
        this.currentStep = 1
        this.selectedAsset = null
        this.tronColorGrey = false
        this.amount = ''
        this.amountHasError = false
        this.isFeesLoadPending = false
        this.walletAddress = ''
        this.requestPayload = {}
      }
    },
    watch: {
      tronColorGrey (value) {
        if (!this.selectedAssetWithoutSpace) return
        if (!value) {
          this.onSetTronWhiteHandler()
        } else {
          this.onSetTronGreyHandler()
        }
      },
      async selectedAssetWithoutSpace (asset) {
        await this.loadSelectedAssetMinAmount(asset)
      }
    }
  }
</script>

<style lang="scss" scoped>
@import '~scss/variables';

.withdrawal-widget {
  &.grey {
    .widget-step {
      /deep/ .md-field {
        &.field__number,
        &.field__wallet {
          border-color: $carbon-black !important;
        }

        &.md-invalid {
          border-color: $error-red !important;
        }
      }
    }
  }

  .invisible {
    height: 0 !important;
  }

  .widget-step {
    /deep/ .step-title {
      cursor: pointer !important;
    }

    /deep/ .md-field {
      &.field__number,
      &.field__wallet {
        margin: 0 !important;
        min-height: 40px !important;
        padding: 4px 10px !important;
        border-color: $light-grey !important;
        background: inherit !important;
      }

      &.field__wallet input {
        font-size: 16px !important;
      }

      &.md-invalid {
        border-color: $error-red !important;
      }
    }
  }
}
</style>

<style lang="scss">
@import '~scss/variables';

.withdrawal-widget {
  &.grey {
    background-color: $gray_asset !important;

    .widget-step {
      &__inner {
        .custom-select {
          --vs-border-color: #2C3030;
          --vs-dropdown-bg: #d6d6d6;

          .vs__dropdown-toggle {
            background-color: inherit;
          }
        }
      }
    }
  }

  .widget-step {
    border-bottom: 1px solid $grey;
    padding: 20px 0;

    &.active {
      .widget-step__inner {
        padding: 10px 0 0 40px;
      }
    }

    &:last-child {
      border-bottom: none;
    }

    &__title {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 1rem;
      font-weight: 400;
      font-size: 18px;
      font-weight: 700;

      div {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }

    &__inner {
      .validation-error {
        color: $col-invalid;
        font-size: 12px;
        display: inline-flex;
        justify-content: flex-end;
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/WithdrawalWidget/index.vue